import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen } from "@phosphor-icons/react";
import { API, COVERS } from "@/lib/site";

export const JournalStrip = () => {
  const [posts, setPosts] = useState([]);
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/blog`)
      .then((r) => r.json())
      .then((d) => setPosts((d.posts || []).filter((p) => p.ebook_lookup_key)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (posts.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % posts.length), 5000);
    return () => clearInterval(t);
  }, [posts, idx]);

  const go = useCallback((p) => p?.slug && navigate(`/blog/${p.slug}`), [navigate]);

  if (!posts.length) return null;
  const p = posts[idx];

  return (
    <section
      id="from-journal"
      data-testid="journal-strip"
      className="relative overflow-hidden border-y border-brand-line bg-brand-surface2/50"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-12">
        <div className="flex items-center gap-2 mb-6 text-brand-gold">
          <BookOpen size={16} weight="fill" />
          <span className="overline">From the journal · read before you buy</span>
        </div>

        <AnimatePresence mode="wait">
          <motion.button
            key={p.slug}
            type="button"
            data-testid={`journal-strip-item`}
            data-index={idx}
            onClick={() => go(p)}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="group w-full flex flex-col sm:flex-row items-center gap-6 text-left"
          >
            {COVERS[p.ebook_lookup_key] && (
              <img
                src={COVERS[p.ebook_lookup_key]}
                alt=""
                className="w-20 h-28 object-cover rounded-lg border border-brand-line shrink-0 shadow-lg"
              />
            )}
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-3 mb-2">
                <span className="overline">{p.tag}</span>
                <span className="text-xs text-brand-dim">· {p.read}</span>
              </div>
              <h3 className="font-serif text-2xl lg:text-3xl text-brand-text group-hover:text-brand-gold transition-colors leading-snug">
                {p.title}
              </h3>
              {p.excerpt && <p className="text-sm text-brand-dim mt-2 line-clamp-2 max-w-2xl">{p.excerpt}</p>}
            </div>
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-line px-5 py-3 text-sm text-brand-text group-hover:border-brand-gold group-hover:text-brand-gold transition-colors shrink-0">
              Read the summary <ArrowRight size={16} />
            </span>
          </motion.button>
        </AnimatePresence>

        {posts.length > 1 && (
          <div className="flex items-center gap-2 mt-8" data-testid="journal-strip-dots">
            {posts.map((_, i) => (
              <button
                key={i}
                data-testid={`journal-strip-dot-${i}`}
                onClick={() => setIdx(i)}
                aria-label={`Show journal item ${i + 1}`}
                aria-current={i === idx}
                className="py-3 px-1 -my-2 group/dot"
              >
                <span
                  className={`block h-1.5 rounded-full transition-all duration-300 ${
                    i === idx ? "w-8 bg-brand-gold" : "w-3 bg-brand-line group-hover/dot:bg-brand-dim"
                  }`}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
